/**
 * Home Page - Main Pokédex grid with competitive view toggle
 * Includes all 1025 Pokémon + 94 Mega Evolutions + 57 Regional Forms
 */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Download, Loader2 } from 'lucide-react';
import { PokemonCard, PokemonCardSkeleton } from '../components/pokedex/PokemonCard';
import { MegaPokemonCard } from '../components/pokedex/MegaPokemonCard';
import { RegionalPokemonCard } from '../components/pokedex/RegionalPokemonCard';
import { AdvancedSearchBar } from '../components/pokedex/AdvancedSearchBar';
import { CompetitiveTable } from '../components/pokedex/CompetitiveTable';
import { usePokemon, GENERATIONS } from '../hooks/usePokemon';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { getMegasAsPokemonList } from '../data/megaEvolutions';
import { getRegionalFormsAsPokemonList } from '../data/regionalForms';
import { getAlternateFormsAsPokemonList } from '../data/alternateForms';
import { FormPokemonCard } from '../components/pokedex/FormPokemonCard';
import { trackEvent } from '../lib/analytics';
import { POKEMON_NAMES_ES } from '../data/pokemonTranslations';

// Number of pokemon to load per batch
const BATCH_SIZE = 40;
const PREFETCH_BATCH = 100; // Prefetch more data for filters

// Pokemon IDs by category
const POKEMON_CATEGORIES = {
  legendary: [144, 145, 146, 150, 243, 244, 245, 249, 250, 377, 378, 379, 380, 381, 382, 383, 384, 386, 480, 481, 482, 483, 484, 485, 486, 487, 488, 491, 492, 493, 638, 639, 640, 641, 642, 643, 644, 645, 646, 716, 717, 718, 785, 786, 787, 788, 789, 790, 791, 792, 800, 888, 889, 890, 891, 892, 894, 895, 896, 897, 898, 905, 1001, 1002, 1003, 1004, 1007, 1008, 1014, 1015, 1016, 1017, 1024, 1025],
  mythical: [151, 251, 385, 489, 490, 492, 493, 494, 647, 648, 649, 719, 720, 721, 801, 802, 807, 808, 809, 893],
  starter: [1, 4, 7, 152, 155, 158, 252, 255, 258, 387, 390, 393, 495, 498, 501, 650, 653, 656, 722, 725, 728, 810, 813, 816, 906, 909, 912],
  physical: [1, 4, 6, 7, 25, 68, 94, 127, 130, 143, 149, 150, 212, 214, 248, 257, 289, 306, 373, 376, 384, 445, 448, 475, 530, 555, 612, 635, 637, 646, 658, 681, 700, 706, 718, 724, 745, 768, 773, 778, 784, 785, 812, 815, 818, 839, 845, 849, 851, 862, 876, 879, 884, 887, 892, 898],
  special: [3, 6, 9, 59, 65, 80, 94, 121, 130, 131, 134, 135, 136, 145, 146, 149, 150, 157, 169, 181, 196, 197, 229, 230, 233, 242, 245, 248, 249, 250, 254, 257, 260, 282, 306, 330, 334, 350, 373, 376, 379, 380, 381, 382, 383, 384, 386, 407, 430, 445, 448, 461, 462, 466, 467, 468, 471, 472, 473, 474, 475, 476, 477, 478, 479, 480, 481, 482, 483, 484, 485, 486, 487, 488]
};

// Parse advanced search query
function parseSearchQuery(query) {
  const result = {
    text: '',
    ability: null,
    type: null,
    damage: null,
    tag: null
  };
  
  if (!query) return result;
  
  // Regex patterns for filters
  const patterns = {
    ability: /\.(?:ability|a)\s+(\S+)/i,
    type: /\.(?:type|t)\s+(\S+)/i,
    damage: /\.(?:damage|dmg)\s+(\S+)/i,
    tag: /\.tag\s+(\S+)/i
  };
  
  let remaining = query;
  
  for (const [key, pattern] of Object.entries(patterns)) {
    const match = remaining.match(pattern);
    if (match) {
      result[key] = match[1].toLowerCase().trim();
      remaining = remaining.replace(match[0], '').trim();
    }
  }
  
  result.text = remaining.trim();
  
  return result;
}

// Export to CSV function
function exportToCSV(pokemon, pokemonData, filename = 'pokemon_export.csv') {
  const headers = ['ID', 'Name', 'Type 1', 'Type 2', 'Ability 1', 'Ability 2', 'Hidden Ability', 'HP', 'Attack', 'Defense', 'Sp. Atk', 'Sp. Def', 'Speed', 'BST', 'Is Mega'];
  
  const rows = pokemon.map(p => {
    // Handle Mega Evolutions
    if (p.isMega) {
      return [
        p.id,
        p.displayName || p.name,
        p.types[0] || '',
        p.types[1] || '',
        p.ability || '',
        '',
        '',
        p.stats?.hp || 0,
        p.stats?.attack || 0,
        p.stats?.defense || 0,
        p.stats?.spAtk || 0,
        p.stats?.spDef || 0,
        p.stats?.speed || 0,
        p.bst || 0,
        'Yes'
      ];
    }
    
    // Handle regular Pokemon
    const data = pokemonData[p.id];
    if (!data) return null;
    
    const types = data.types?.map(t => t.type.name) || [];
    const abilities = data.abilities?.filter(a => !a.is_hidden).map(a => a.ability.name) || [];
    const hiddenAbility = data.abilities?.find(a => a.is_hidden)?.ability.name || '';
    const stats = data.stats || [];
    const getStatValue = (name) => stats.find(s => s.stat.name === name)?.base_stat || 0;
    const bst = stats.reduce((sum, s) => sum + s.base_stat, 0);
    
    return [
      p.id,
      p.name,
      types[0] || '',
      types[1] || '',
      abilities[0] || '',
      abilities[1] || '',
      hiddenAbility,
      getStatValue('hp'),
      getStatValue('attack'),
      getStatValue('defense'),
      getStatValue('special-attack'),
      getStatValue('special-defense'),
      getStatValue('speed'),
      bst,
      'No'
    ];
  }).filter(Boolean);
  
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
  ].join('\n');
  
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

export default function Home() {
  const { t, addToCache, getFromCache, language } = useApp();
  const { getPokemonList, getPokemon } = usePokemon();
  
  // State
  const [allPokemon, setAllPokemon] = useState([]);
  const [pokemonData, setPokemonData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [displayCount, setDisplayCount] = useState(BATCH_SIZE);
  const [prefetching, setPrefetching] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedGeneration, setSelectedGeneration] = useState('all');
  const [sortBy, setSortBy] = useState('number');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'competitive'
  const [formFilter, setFormFilter] = useState('all'); // 'all', 'regular', 'mega', 'regional'
  
  // Ref for infinite scroll
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const prefetchedRef = useRef(new Set());
  
  // Get Mega Evolutions and Regional Forms data
  const megaEvolutions = useMemo(() => getMegasAsPokemonList(), []);
  const regionalForms = useMemo(() => getRegionalFormsAsPokemonList(), []);
  const alternateForms = useMemo(() => getAlternateFormsAsPokemonList(), []);
  
  // Fetch initial pokemon list and combine with Megas and Regional Forms
  useEffect(() => {
    let mounted = true;
    
    const fetchList = async () => {
      setLoading(true);
      const list = await getPokemonList();
      if (mounted) {
        // Combine regular Pokemon with Mega Evolutions and Regional Forms
        const combinedList = [
          ...list,
          ...megaEvolutions,
          ...regionalForms,
          ...alternateForms
        ];
        setAllPokemon(combinedList);
        setLoading(false);
      }
    };
    
    fetchList();
    return () => { mounted = false; };
  }, [getPokemonList, megaEvolutions, regionalForms]);
  
  // Fetch pokemon data for filtering
  const fetchPokemonData = useCallback(async (pokemon) => {
    // Skip fetching for Mega Evolutions and Regional Forms (they have their own data)
    if (pokemon.isMega || pokemon.isRegional) return;
    
    if (prefetchedRef.current.has(pokemon.id)) return;
    
    const cached = getFromCache(`pokemon-${pokemon.id}`);
    if (cached) {
      setPokemonData(prev => ({ ...prev, [pokemon.id]: cached }));
      prefetchedRef.current.add(pokemon.id);
      return;
    }
    
    const data = await getPokemon(pokemon.id);
    if (data) {
      addToCache(`pokemon-${pokemon.id}`, data);
      setPokemonData(prev => ({ ...prev, [pokemon.id]: data }));
      prefetchedRef.current.add(pokemon.id);
    }
  }, [getPokemon, addToCache, getFromCache]);
  
  // Prefetch data for ability filtering - load in batches
  const prefetchForAbilitySearch = useCallback(async () => {
    if (prefetching || allPokemon.length === 0) return;
    
    setPrefetching(true);
    const unfetched = allPokemon.filter(p => !p.isMega && !p.isRegional && !prefetchedRef.current.has(p.id));
    const batch = unfetched.slice(0, PREFETCH_BATCH);
    
    // Fetch in parallel batches of 10
    for (let i = 0; i < batch.length; i += 10) {
      const chunk = batch.slice(i, i + 10);
      await Promise.all(chunk.map(p => fetchPokemonData(p)));
    }
    
    setPrefetching(false);
  }, [allPokemon, prefetching, fetchPokemonData]);
  
  // Trigger prefetch when ability search is used
  useEffect(() => {
    const hasAbilitySearch = searchQuery.includes('.a ') || searchQuery.includes('.ability ');
    if (hasAbilitySearch && allPokemon.length > 0) {
      prefetchForAbilitySearch();
    }
  }, [searchQuery, allPokemon, prefetchForAbilitySearch]);
  
  // Fetch data when filters require it or competitive mode is active
  useEffect(() => {
    if (selectedType !== 'all' || sortBy === 'stats' || viewMode === 'competitive' || searchQuery.includes('.')) {
      const visiblePokemon = allPokemon.filter(p => !p.isMega && !p.isRegional && !p.isAlternate).slice(0, displayCount);
      visiblePokemon.forEach(pokemon => {
        if (!pokemonData[pokemon.id]) {
          fetchPokemonData(pokemon);
        }
      });
    }
  }, [allPokemon, displayCount, selectedType, sortBy, viewMode, searchQuery, pokemonData, fetchPokemonData]);
  
  // Track searches (debounced)
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) return;
    const timer = setTimeout(() => {
      trackEvent('search', { query: searchQuery });
    }, 1200);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Parse search query
  const parsedQuery = useMemo(() => parseSearchQuery(searchQuery), [searchQuery]);
  
  // Filter and sort pokemon
  const filteredPokemon = useMemo(() => {
    let result = [...allPokemon];
    
    // Form filter (regular, mega, regional)
    if (formFilter === 'regular') {
      result = result.filter(p => !p.isMega && !p.isRegional);
    } else if (formFilter === 'mega') {
      result = result.filter(p => p.isMega);
    } else if (formFilter === 'regional') {
      result = result.filter(p => p.isRegional);
    } else if (formFilter === 'alola') {
      result = result.filter(p => p.isRegional && p.region === 'alola');
    } else if (formFilter === 'galar') {
      result = result.filter(p => p.isRegional && p.region === 'galar');
    } else if (formFilter === 'hisui') {
      result = result.filter(p => p.isRegional && p.region === 'hisui');
    } else if (formFilter === 'paldea') {
      result = result.filter(p => p.isRegional && p.region === 'paldea');
    }
    
    // Text search filter
    if (parsedQuery.text) {
      const query = parsedQuery.text.toLowerCase().trim();
      const isNumericQuery = /^\d+$/.test(query);
      
      if (isNumericQuery) {
        const exactNum = parseInt(query, 10);
        // For megas and regionals, search by baseId as well
        const exactMatch = result.find(p => p.id === exactNum || ((p.isMega || p.isRegional) && p.baseId === exactNum));
        if (exactMatch) {
          result = [exactMatch];
        } else {
          result = result.filter(p => 
            String(p.id).startsWith(query) || 
            ((p.isMega || p.isRegional) && String(p.baseId).startsWith(query))
          );
        }
      } else {
        result = result.filter(p => {
          const searchName = (p.isMega || p.isRegional)
            ? (p.displayName || p.name || '').toLowerCase()
            : p.name.toLowerCase();
          const baseName = (p.isMega || p.isRegional) ? p.baseName?.toLowerCase() : '';
          const regionName = p.isRegional ? p.region?.toLowerCase() : '';
          // Spanish names for paradox Pokémon and special cases
          const spanishName = POKEMON_NAMES_ES[p.name]?.toLowerCase() || '';
          return searchName.includes(query) || baseName.includes(query) || regionName.includes(query) || spanishName.includes(query);
        });
      }
    }
    
    // Type filter (from dropdown or .type query)
    const typeFilter = parsedQuery.type || (selectedType !== 'all' ? selectedType : null);
    if (typeFilter) {
      result = result.filter(p => {
        if (p.isMega || p.isRegional) {
          return p.types?.some(t => t.includes(typeFilter));
        }
        const data = pokemonData[p.id];
        if (!data) return false;
        return data.types?.some(t => t.type.name.includes(typeFilter));
      });
    }
    
    // Ability filter (.ability query)
    if (parsedQuery.ability) {
      result = result.filter(p => {
        if (p.isMega) {
          return p.ability?.toLowerCase().includes(parsedQuery.ability);
        }
        if (p.isRegional) return false; // Regional forms don't have ability data locally
        const data = pokemonData[p.id];
        if (!data) return false;
        return data.abilities?.some(a => 
          a.ability.name.toLowerCase().includes(parsedQuery.ability)
        );
      });
    }
    
    // Damage category filter (.damage query)
    if (parsedQuery.damage) {
      const dmg = parsedQuery.damage;
      if (dmg === 'physical' || dmg === 'phys') {
        result = result.filter(p => !p.isMega && !p.isRegional && POKEMON_CATEGORIES.physical.includes(p.id));
      } else if (dmg === 'special' || dmg === 'spec') {
        result = result.filter(p => !p.isMega && !p.isRegional && POKEMON_CATEGORIES.special.includes(p.id));
      }
    }
    
    // Tag filter (.tag query) - legendary, mythical, starter, mega, regional
    if (parsedQuery.tag) {
      const tag = parsedQuery.tag;
      
      if (tag === 'legendary' || tag === 'legend') {
        result = result.filter(p => !p.isMega && !p.isRegional && POKEMON_CATEGORIES.legendary.includes(p.id));
      } else if (tag === 'mythical' || tag === 'myth') {
        result = result.filter(p => !p.isMega && !p.isRegional && POKEMON_CATEGORIES.mythical.includes(p.id));
      } else if (tag === 'starter' || tag === 'start') {
        result = result.filter(p => !p.isMega && !p.isRegional && POKEMON_CATEGORIES.starter.includes(p.id));
      } else if (tag === 'mega' || tag === 'megas') {
        result = result.filter(p => p.isMega);
      } else if (tag === 'regional' || tag === 'region') {
        result = result.filter(p => p.isRegional);
      } else if (tag === 'alola' || tag === 'alolan') {
        result = result.filter(p => p.isRegional && p.region === 'alola');
      } else if (tag === 'galar' || tag === 'galarian') {
        result = result.filter(p => p.isRegional && p.region === 'galar');
      } else if (tag === 'hisui' || tag === 'hisuian') {
        result = result.filter(p => p.isRegional && p.region === 'hisui');
      } else if (tag === 'paldea' || tag === 'paldean') {
        result = result.filter(p => p.isRegional && p.region === 'paldea');
      }
    }
    
    // Generation filter - applies to regular Pokemon and regional forms by base ID
    if (selectedGeneration !== 'all') {
      const gen = GENERATIONS[selectedGeneration];
      if (gen) {
        result = result.filter(p => {
          if (p.isMega || p.isRegional) {
            // Filter by base Pokemon's generation
            return p.baseId >= gen.start && p.baseId <= gen.end;
          }
          return p.id >= gen.start && p.id <= gen.end;
        });
      }
    }
    
    // Sort
    switch (sortBy) {
      case 'name':
        result.sort((a, b) => {
          const nameA = (a.isMega || a.isRegional) ? (a.displayName || a.name) : a.name;
          const nameB = (b.isMega || b.isRegional) ? (b.displayName || b.name) : b.name;
          return nameA.localeCompare(nameB);
        });
        break;
      case 'stats':
        result.sort((a, b) => {
          // Megas have BST, regionals don't - put regionals with their base
          if (a.isMega && b.isMega) {
            return (b.bst || 0) - (a.bst || 0);
          }
          if (a.isMega) {
            const dataB = pokemonData[b.id];
            const totalB = dataB?.stats?.reduce((sum, s) => sum + s.base_stat, 0) || 0;
            return (a.bst || 0) - totalB > 0 ? -1 : 1;
          }
          if (b.isMega) {
            const dataA = pokemonData[a.id];
            const totalA = dataA?.stats?.reduce((sum, s) => sum + s.base_stat, 0) || 0;
            return totalA - (b.bst || 0) > 0 ? -1 : 1;
          }
          // Regional forms use base ID for sorting
          if (a.isRegional || b.isRegional) {
            const idA = a.isRegional ? a.baseId : a.id;
            const idB = b.isRegional ? b.baseId : b.id;
            return idA - idB;
          }
          const dataA = pokemonData[a.id];
          const dataB = pokemonData[b.id];
          if (!dataA || !dataB) return 0;
          const totalA = dataA.stats?.reduce((sum, s) => sum + s.base_stat, 0) || 0;
          const totalB = dataB.stats?.reduce((sum, s) => sum + s.base_stat, 0) || 0;
          return totalB - totalA;
        });
        break;
      default: // number
        result.sort((a, b) => {
          // Regular Pokemon first by ID, then special forms sorted by their base ID
          // Order: Base -> Regional -> Mega
          const getOrder = (p) => {
            if (p.isRegional) return p.baseId + 0.3;
            if (p.isMega) return p.baseId + 0.6;
            if (p.isAlternate) return p.baseId + 0.8;
            return p.id;
          };
          
          const orderA = getOrder(a);
          const orderB = getOrder(b);
          
          if (orderA === orderB) {
            // Tie-breaker for multiple megas or regionals of the same base Pokemon
            // For example, Mega Charizard X vs Mega Charizard Y
            return a.id - b.id;
          }
          
          return orderA - orderB;
        });
    }
    
    return result;
  }, [allPokemon, parsedQuery, selectedType, selectedGeneration, sortBy, pokemonData, formFilter]);
  
  // Pokemon to display (with pagination)
  const displayedPokemon = useMemo(() => {
    return filteredPokemon.slice(0, displayCount);
  }, [filteredPokemon, displayCount]);
  
  // Handle CSV export
  const handleExport = useCallback(async () => {
    setExporting(true);
    
    // Fetch all data for filtered pokemon if not already loaded
    const toFetch = filteredPokemon.filter(p => !p.isMega && !pokemonData[p.id]);
    for (let i = 0; i < toFetch.length; i += 20) {
      const batch = toFetch.slice(i, i + 20);
      await Promise.all(batch.map(p => fetchPokemonData(p)));
    }
    
    // Wait a bit for state to update
    setTimeout(() => {
      exportToCSV(filteredPokemon, pokemonData, `dexriddle_${filteredPokemon.length}_pokemon.csv`);
      trackEvent('csv_export', { count: filteredPokemon.length });
      setExporting(false);
    }, 500);
  }, [filteredPokemon, pokemonData, fetchPokemonData]);
  
  // Infinite scroll
  useEffect(() => {
    if (loadMoreRef.current && !loading) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && displayCount < filteredPokemon.length && !loadingMore) {
            setLoadingMore(true);
            setTimeout(() => {
              setDisplayCount(prev => Math.min(prev + BATCH_SIZE, filteredPokemon.length));
              setLoadingMore(false);
            }, 300);
          }
        },
        { threshold: 0.1 }
      );
      
      observerRef.current.observe(loadMoreRef.current);
    }
    
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, displayCount, filteredPokemon.length, loadingMore]);
  
  // Reset display count when filters change
  useEffect(() => {
    setDisplayCount(BATCH_SIZE);
  }, [searchQuery, selectedType, selectedGeneration, sortBy, formFilter]);
  
  // Count loaded pokemon for ability filter
  const loadedCount = Object.keys(pokemonData).length;
  const regularPokemonCount = allPokemon.filter(p => !p.isMega && !p.isRegional).length;
  const showLoadingIndicator = prefetching || (parsedQuery.ability && loadedCount < regularPokemonCount);
  
  // Count forms in current filter
  const megaCount = filteredPokemon.filter(p => p.isMega).length;
  const regionalCount = filteredPokemon.filter(p => p.isRegional).length;
  const regularCount = filteredPokemon.filter(p => !p.isMega && !p.isRegional).length;
  
  return (
    <div className="min-h-screen" data-testid="home-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Hero section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-6"
        >
          {/* Logo image */}
          <img
            src="/logo-dexriddle-new.png"
            alt="DexRiddle Logo"
            className="h-20 sm:h-28 lg:h-32 mx-auto mb-3"
            data-testid="home-logo"
          />
          <p className="text-sm text-muted-foreground mb-2">
            {language === 'es' 
              ? 'De los creadores de '
              : 'From the creators of '
            }
            <a 
              href="https://pokeriddle.net" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-violet-600 dark:text-violet-400 hover:underline font-medium"
            >
              PokeRiddle.net
            </a>
          </p>
          <p className="text-muted-foreground text-base sm:text-lg">
            {language === 'es'
              ? 'Tu Pokédex moderna. Inspírate para crear tus equipos en Pokémon Champions.'
              : 'Your modern Pokédex with all Pokémon. Get inspired to create your teams in Pokémon Champions.'
            }
          </p>
        </motion.div>
        
        {/* Advanced Search and filters */}
        <AdvancedSearchBar
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          selectedType={selectedType}
          setSelectedType={setSelectedType}
          selectedGeneration={selectedGeneration}
          setSelectedGeneration={setSelectedGeneration}
          sortBy={sortBy}
          setSortBy={setSortBy}
          viewMode={viewMode}
          setViewMode={setViewMode}
          formFilter={formFilter}
          setFormFilter={setFormFilter}
        />
        
        {/* Results count and export */}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground">
              {filteredPokemon.length} Pokémon
              {formFilter === 'all' && (megaCount > 0 || regionalCount > 0) && (
                <span className="ml-1">
                  <span className="text-muted-foreground">({regularCount}</span>
                  {megaCount > 0 && <span className="text-yellow-600 dark:text-yellow-400"> + {megaCount} Mega</span>}
                  {regionalCount > 0 && <span className="text-cyan-600 dark:text-cyan-400"> + {regionalCount} Regional</span>}
                  <span className="text-muted-foreground">)</span>
                </span>
              )}
            </p>
            {showLoadingIndicator && (
              <span className="flex items-center gap-1 text-xs text-violet-600 dark:text-violet-400">
                <Loader2 className="w-3 h-3 animate-spin" />
                {language === 'es' ? 'Cargando datos...' : 'Loading data...'}
                ({loadedCount}/{regularPokemonCount})
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {viewMode === 'competitive' && (
              <span className="text-xs text-muted-foreground hidden sm:inline">
                {language === 'es' ? 'Modo Competitivo' : 'Competitive Mode'}
              </span>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={handleExport}
              disabled={exporting || filteredPokemon.length === 0}
              data-testid="export-csv-btn"
              className="text-xs"
            >
              {exporting ? (
                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <Download className="w-3 h-3 mr-1" />
              )}
              CSV
            </Button>
          </div>
        </div>
        
        {/* Pokemon display */}
        {loading ? (
          viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {Array.from({ length: 20 }).map((_, i) => (
                <PokemonCardSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <div className="animate-spin w-8 h-8 border-4 border-violet-500 border-t-transparent rounded-full mx-auto mb-4" />
              <p className="text-muted-foreground">{t('loading')}</p>
            </div>
          )
        ) : displayedPokemon.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-xl text-muted-foreground">{t('noResults')}</p>
            {showLoadingIndicator && (
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'es' 
                  ? 'Espera mientras cargamos más datos para la búsqueda...'
                  : 'Please wait while we load more data for the search...'
                }
              </p>
            )}
          </div>
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {displayedPokemon.map((pokemon, index) => {
                  if (pokemon.isMega) {
                    return (
                      <MegaPokemonCard 
                        key={`mega-${pokemon.id}`} 
                        pokemon={pokemon}
                        index={index}
                      />
                    );
                  }
                  if (pokemon.isRegional) {
                    return (
                      <RegionalPokemonCard
                        key={`regional-${pokemon.id}`}
                        pokemon={pokemon}
                        index={index}
                      />
                    );
                  }
                  if (pokemon.isAlternate) {
                    return (
                      <FormPokemonCard
                        key={`form-${pokemon.id}`}
                        pokemon={pokemon}
                        index={index}
                      />
                    );
                  }
                  return (
                    <PokemonCard
                      key={pokemon.id} 
                      pokemon={pokemon}
                      index={index}
                    />
                  );
                })}
              </div>
            ) : (
              <CompetitiveTable 
                pokemon={filteredPokemon}
                displayCount={displayCount}
              />
            )}
            
            {/* Load more trigger */}
            {displayCount < filteredPokemon.length && (
              <div 
                ref={loadMoreRef}
                className="flex justify-center py-8"
              >
                {loadingMore && (
                  <div className="flex gap-2">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <motion.div
                        key={i}
                        className="w-3 h-3 bg-violet-500 rounded-full"
                        animate={{ y: [0, -10, 0] }}
                        transition={{ duration: 0.5, delay: i * 0.1, repeat: Infinity }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
